// 操作画面の表示言語（日本語 / English）
import { useEffect, useState } from 'react';

export type UiLang = 'ja' | 'en';
const KEY = 'amo_ui_lang';

function initLang(): UiLang {
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q === 'en' || q === 'ja') { localStorage.setItem(KEY, q); return q; }
    const saved = localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'ja') return saved;
  } catch { /* ignore */ }
  return (navigator.language || '').toLowerCase().startsWith('ja') ? 'ja' : 'en';
}

let current: UiLang = initLang();
const listeners = new Set<() => void>();

export function getLang(): UiLang { return current; }
export function setLang(l: UiLang): void {
  current = l;
  try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  document.documentElement.lang = l;
  listeners.forEach((f) => f());
}
/** 画面の部品で呼ぶと、言語の切り替えで再描画される */
export function useLang(): UiLang {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((x) => x + 1);
    listeners.add(f);
    return () => { listeners.delete(f); };
  }, []);
  return current;
}
/** t('日本語', 'English') */
export const t = (ja: string, en: string): string => (current === 'en' ? en : ja);

/** サーバーから届く日本語メッセージを英語にする */
const SERVER_EN: Record<string, string> = {
  '1日のイベント作成数の上限に達しました。翌日以降にお試しください。': 'You have reached the daily limit for creating events. Please try again tomorrow.',
  'Authorization: Bearer ヘッダーが必要です。': 'Authorization header is required.',
  'OpenAI接続エラーが発生しました': 'Could not connect to the translation engine.',
  'このイベントはすでに管理トークンが設定されています。': 'This event already has a management token.',
  'このイベントは無効です。': 'This event is no longer active.',
  'イベントが登録されていません。': 'This event is not registered.',
  '契約時間が終了しました。翻訳配信は停止しました。': 'Your purchased time has ended. Translation has stopped.',
  '延長が適用されました。': 'Your extension has been applied.',
  '本日の接続テスト回数が上限に達しました。': 'You have reached today\'s limit for connection tests.',
  '決済が完了していません。': 'Payment has not been completed.',
  '猶予に入りました。あと15分利用できます。': 'Grace period started. You can continue for 15 more minutes.',
  '管理者パスワードが違います。セットアップ画面からもう一度お試しください。': 'Incorrect admin password. Please try again from the setup page.',
  '購入済みの利用時間が終了しました。': 'Your purchased time has ended.',
  '購入済みの利用時間（猶予含む）が終了しました。': 'Your purchased time (including grace period) has ended.',
};
export function trServer(msg: string | null | undefined): string {
  if (!msg) return msg ?? '';
  if (current !== 'en') return msg;
  if (SERVER_EN[msg]) return SERVER_EN[msg];
  let m = msg.match(/^契約時間まであと(\d+)分を切りました。$/);
  if (m) return `Less than ${m[1]} minutes of purchased time remaining.`;
  m = msg.match(/^接続失敗: (.*)$/);
  if (m) return `Connection failed: ${m[1]}`;
  return msg;
}

/** 右上の「日本語 / EN」切り替え */
export function LangSwitch({ dark = false }: { dark?: boolean }) {
  const lang = useLang();
  const base = { padding: '4px 10px', fontSize: '0.8rem', fontWeight: 700, border: 'none', cursor: 'pointer', background: 'transparent' } as const;
  const on = { background: dark ? '#fff' : '#1f2937', color: dark ? '#111827' : '#fff', borderRadius: 999 };
  const off = { color: dark ? '#cbd5e1' : '#4b5563' };
  return (
    <div style={{ display: 'inline-flex', border: `1px solid ${dark ? 'rgba(255,255,255,.35)' : '#d1d5db'}`, borderRadius: 999, padding: 2 }} role="group" aria-label="Language">
      <button type="button" style={{ ...base, ...(lang === 'ja' ? on : off) }} onClick={() => setLang('ja')}>日本語</button>
      <button type="button" style={{ ...base, ...(lang === 'en' ? on : off) }} onClick={() => setLang('en')}>EN</button>
    </div>
  );
}
